import { Property } from 'csstype';

// Log severity level
type Severity = 'debug' | 'error' | 'info' | 'warn';

/**
 * Get CSS color associated with the specified log message severity
 *
 * @param {Severity} severity Log message severity
 */
const getSeverityColor = (severity: Severity): Property.Color => {
  switch (severity) {
    case 'debug':
      return '#16697a';
    case 'info':
      return '#1a73e8';
    case 'error':
      return '#e5383b';
    case 'warn':
      return '#f2ab26';
  }
};

/**
 * Prepare category string to support styling it
 *
 * @param {string} category Target category name
 */
const getCategory = (category: string) => `%c [${category}]`;

/**
 * Create a CSS string containing styles to apply to the message category
 *
 * @param {Severity} severity Log message severity
 */
const getCategoryStyle = (severity: Severity) => {
  // Determine a custom header color
  const color = getSeverityColor(severity);

  // Build a list of base CSS styles to apply to the header
  const styles = [`color: ${color}`, 'font-weight: bold'];

  return styles.join(';');
};

export default {
  /**
   * Log a debug message to browser's console
   *
   * @param {string} category Message prefix category
   * @param {string} message Message to log
   * @param {Array} params Additional optional values to log
   */
  debug: (category: string, message: string, ...params: Array<unknown>) =>
    console.log(getCategory(category), getCategoryStyle('debug'), message, ...params),

  /**
   * Log an informational message to browser's console
   *
   * @param {string} category Message prefix category
   * @param {string} message Message to log
   * @param {Array} params Additional optional values to log
   */
  info: (category: string, message: string, ...params: Array<unknown>) =>
    console.log(getCategory(category), getCategoryStyle('info'), message, ...params),

  /**
   * Log an error message to browser's console
   *
   * @param {string} category Message prefix category
   * @param {string} message Message to log
   * @param {Array} params Additional optional values to log
   */
  error: (category: string, message: string, ...params: Array<unknown>) =>
    console.log(getCategory(category), getCategoryStyle('error'), message, ...params),

  /**
   * Log a warning message to browser's console
   *
   * @param {string} category Message prefix category
   * @param {string} message Message to log
   * @param {Array} params Additional optional values to log
   */
  warn: (category: string, message: string, ...params: Array<unknown>) =>
    console.log(getCategory(category), getCategoryStyle('warn'), message, ...params),
};
